import { useEffect, useMemo, useState } from 'preact/hooks';
import { Award, ChevronRight, DoorOpen, RotateCcw, ScrollText, Sparkles, Ticket } from 'lucide-preact';
import { GADGETS, MODE_INFO, PETS, SHOP_WEAPONS } from '@vm/content';
import type { MatchRewards, RewardBundle } from '@vm/shared';
import { audio } from '../../game/audio/Audio';
import { hud } from '../../game/hud/state';
import { mapName } from '../state/content';
import { go, home, route } from '../state/nav';
import { quickPlay, pendingLevelUp } from '../state/play';
import { prefs } from '../state/prefs';
import { catalog, currentMatch, lastRewards } from '../state/store';
import { fmtNum } from '../ui/format';
import { useCountUp } from '../ui/hooks';
import { PlayOfTheGame } from './Highlights';
import { CoinIcon, LevelBadge, Modal, RewardChips, Spinner, WeaponImg } from '../ui/kit';
import { Icon } from '../ui/icons';
import { PetPreview } from '../ui/PetPreview';

/** Falling voxel confetti (pure CSS). */
export function Confetti(props: { count?: number }) {
  const bits = useMemo(
    () =>
      Array.from({ length: props.count ?? 46 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.9,
        dur: 1.8 + Math.random() * 1.6,
        size: 6 + Math.round(Math.random() * 8),
        color: ['#ffd24a', '#ff5a4a', '#4a9aff', '#52d86a', '#b46aff', '#4af0d0', '#ff9a3a'][i % 7],
        spin: Math.round(Math.random() * 720 - 360),
      })),
    [],
  );
  return (
    <div class="confetti" aria-hidden="true">
      {bits.map((b, i) => (
        <i
          key={i}
          style={{
            left: `${b.left}%`,
            width: `${b.size}px`,
            height: `${b.size}px`,
            background: b.color,
            animationDelay: `${b.delay}s`,
            animationDuration: `${b.dur}s`,
            '--spin': `${b.spin}deg`,
          } as Record<string, string>}
        />
      ))}
    </div>
  );
}

function xpNeeded(level: number, fallback: number): number {
  return catalog.value?.levels.find((l) => l.level === level)?.xpToNext ?? fallback;
}

interface Segment {
  level: number;
  from: number;
  to: number;
  max: number;
}

function xpSegments(r: MatchRewards): Segment[] {
  const after = r.progression;
  if (r.levelAfter <= r.levelBefore) {
    return [{ level: after.level, from: Math.max(0, after.xpInLevel - r.xp), to: after.xpInLevel, max: after.xpToNext }];
  }
  const segs: Segment[] = [];
  let remaining = r.xp - after.xpInLevel;
  const mids: Segment[] = [];
  for (let lv = r.levelAfter - 1; lv > r.levelBefore; lv--) {
    const need = xpNeeded(lv, after.xpToNext);
    remaining -= need;
    mids.unshift({ level: lv, from: 0, to: need, max: need });
  }
  const firstMax = xpNeeded(r.levelBefore, after.xpToNext);
  segs.push({ level: r.levelBefore, from: Math.max(0, Math.min(firstMax, firstMax - remaining)), to: firstMax, max: firstMax });
  segs.push(...mids);
  segs.push({ level: after.level, from: 0, to: after.xpInLevel, max: after.xpToNext });
  return segs;
}

/** XP bar that fills segment by segment, bursting on every level up. */
function XpAnimation(props: { rewards: MatchRewards; onDone: () => void }) {
  const segs = useMemo(() => xpSegments(props.rewards), [props.rewards.matchId]);
  const [idx, setIdx] = useState(0);
  const [fill, setFill] = useState(segs[0].from / segs[0].max);
  const [burst, setBurst] = useState(0);
  useEffect(() => {
    let raf = 0;
    let cancelled = false;
    const run = (i: number): void => {
      if (cancelled) return;
      if (i >= segs.length) {
        props.onDone();
        return;
      }
      const s = segs[i];
      setIdx(i);
      const t0 = performance.now();
      const dur = 650 + 500 * ((s.to - s.from) / Math.max(1, s.max));
      const step = (t: number): void => {
        if (cancelled) return;
        const k = Math.min(1, (t - t0) / dur);
        const e = 1 - Math.pow(1 - k, 2.2);
        setFill((s.from + (s.to - s.from) * e) / s.max);
        if (k < 1) raf = requestAnimationFrame(step);
        else if (i < segs.length - 1) {
          audio.play('levelup');
          setBurst((b) => b + 1);
          setTimeout(() => {
            setFill(0);
            run(i + 1);
          }, 550);
        } else run(i + 1);
      };
      raf = requestAnimationFrame(step);
    };
    const start = setTimeout(() => run(0), 450);
    return () => {
      cancelled = true;
      clearTimeout(start);
      cancelAnimationFrame(raf);
    };
  }, [props.rewards.matchId]);
  const s = segs[Math.min(idx, segs.length - 1)];
  const xp = useCountUp(props.rewards.xp, 1400);
  return (
    <div class="res-xp">
      <div class={`res-xp-level ${burst ? 'is-burst' : ''}`} key={burst}>
        <LevelBadge level={s.level} size="lg" />
      </div>
      <div class="res-xp-main">
        <div class="res-xp-top">
          <b>+{fmtNum(xp)} XP</b>
          <span>
            {fmtNum(Math.round(fill * s.max))} / {fmtNum(s.max)}
          </span>
        </div>
        <div class="ui-bar xp res-xp-bar">
          <div class="ui-bar-fill" style={{ width: `${Math.min(100, fill * 100)}%` }} />
        </div>
      </div>
    </div>
  );
}

function newUnlocks(from: number, to: number): { weapons: string[]; gadgets: string[]; pets: { id: string; name: string }[] } {
  return {
    weapons: SHOP_WEAPONS.filter((w) => w.unlockLevel > from && w.unlockLevel <= to).map((w) => w.id),
    gadgets: GADGETS.filter((g) => g.unlockLevel > from && g.unlockLevel <= to).map((g) => g.name),
    pets: PETS.filter((p) => p.unlockLevel > from && p.unlockLevel <= to).map((p) => ({ id: p.id, name: p.name })),
  };
}

export function LevelUpPanel(props: { from: number; to: number; rewards: RewardBundle[]; onClose: () => void }) {
  const unlocks = newUnlocks(props.from, props.to);
  const merged: RewardBundle = props.rewards.reduce<RewardBundle>(
    (a, r) => ({
      coins: (a.coins ?? 0) + (r.coins ?? 0),
      gems: (a.gems ?? 0) + (r.gems ?? 0),
      items: [...(a.items ?? []), ...(r.items ?? [])],
    }),
    {},
  );
  useEffect(() => {
    audio.play('levelup');
  }, []);
  return (
    <Modal size="md" class="levelup" onClose={props.onClose} hideClose>
      <Confetti />
      <div class="levelup-inner">
        <div class="levelup-kicker">LEVEL UP!</div>
        <div class="levelup-badge">
          <LevelBadge level={props.to} size="lg" />
        </div>
        <RewardChips reward={merged} class="center" />
        {unlocks.weapons.length > 0 && (
          <div class="levelup-unlocks">
            <h4>New in the Armory</h4>
            <div class="levelup-weapons">
              {unlocks.weapons.map((id) => (
                <WeaponImg key={id} weaponId={id} scale={3} />
              ))}
            </div>
          </div>
        )}
        {unlocks.gadgets.length > 0 && <p class="muted">New gadgets: {unlocks.gadgets.join(', ')}</p>}
        {unlocks.pets.length > 0 && (
          <div class="levelup-unlocks">
            <h4>{unlocks.pets.length > 1 ? 'New pets' : 'New pet'} in the Armory</h4>
            <div class="levelup-pets">
              {unlocks.pets.map((p) => (
                <span key={p.id} class="levelup-pet">
                  <PetPreview petId={p.id} size={64} />
                  <b>{p.name}</b>
                </span>
              ))}
            </div>
          </div>
        )}
        <button class="btn primary big" onClick={props.onClose}>
          Awesome!
        </button>
      </div>
    </Modal>
  );
}

function signed(n: number): string {
  return `${n < 0 ? '−' : '+'}${fmtNum(Math.abs(n))}`;
}

function Breakdown(props: { r: MatchRewards }) {
  return (
    <div class="res-breakdown">
      {props.r.breakdown.map((b, i) => (
        <div key={b.label} class="res-row" style={{ animationDelay: `${0.5 + i * 0.12}s` }}>
          <span>{b.label}</span>
          <span class={`res-row-xp ${b.xp < 0 ? 'neg' : ''}`}>{b.xp ? `${signed(b.xp)} XP` : ''}</span>
          <span class={`res-row-coins ${b.coins < 0 ? 'neg' : ''}`}>
            {b.coins ? (
              <>
                <CoinIcon size={16} /> {signed(b.coins)}
              </>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ResultsScreen() {
  const matchId = route.value.params.matchId;
  const r = lastRewards.value && (!matchId || lastRewards.value.matchId === matchId) ? lastRewards.value : null;
  const end = hud.matchEnd.value;
  const cm = currentMatch.value;
  const mode = r?.mode ?? cm?.match.mode ?? 'tdm';
  const won = r ? r.won : end?.won ?? false;
  const [waitedOut, setWaitedOut] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [animDone, setAnimDone] = useState(false);
  const coins = useCountUp(r?.coins ?? 0, 1300);
  useEffect(() => {
    const t = setTimeout(() => setWaitedOut(true), 12000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    // The results screen celebrates level-ups itself.
    if (r && pendingLevelUp.value) pendingLevelUp.value = null;
  }, [r?.matchId]);
  const mine = end?.scoreboard.find((l) => l.id === hud.myId.value);
  return (
    <div class={`res ${won ? 'is-win' : 'is-loss'}`}>
      {won && <Confetti count={30} />}
      <div class="res-head">
        <div class="res-banner">{won ? 'VICTORY' : end || r ? 'DEFEAT' : 'MATCH OVER'}</div>
        <div class="res-sub">
          <Icon name={MODE_INFO[mode]?.icon ?? 'swords'} size={18} /> {MODE_INFO[mode]?.name ?? mode}
          {cm?.match.mapId && <> · {mapName(cm.match.mapId)}</>}
          {r && r.placement > 0 && <> · #{r.placement}</>}
        </div>
      </div>
      <div class="res-body">
        {mine && (
          <div class="res-stats panel">
            <div>
              <b>{mine.kills}</b>
              <span>Takedowns</span>
            </div>
            <div>
              <b>{mine.deaths}</b>
              <span>Shattered</span>
            </div>
            <div>
              <b>{mine.assists}</b>
              <span>Assists</span>
            </div>
            <div>
              <b>{mine.score}</b>
              <span>Score</span>
            </div>
          </div>
        )}
        <div class="res-main panel">
          {!r ? (
            waitedOut ? (
              <div class="res-wait">
                <Sparkles size={36} />
                <b>Your rewards are on the way!</b>
                <span class="muted">They'll land in your profile in a moment.</span>
              </div>
            ) : (
              <Spinner label="Tallying rewards…" />
            )
          ) : (
            <>
              <XpAnimation
                rewards={r}
                onDone={() => {
                  if (animDone) return;
                  setAnimDone(true);
                  if (r.levelAfter > r.levelBefore) setTimeout(() => setCelebrate(true), 450);
                }}
              />
              <Breakdown r={r} />
              <div class="res-totals">
                <div class="res-total coins">
                  <CoinIcon size={28} />
                  <b>+{fmtNum(coins)}</b>
                </div>
                {r.bpXp > 0 && (
                  <div class="res-total passxp">
                    <Ticket size={24} />
                    <b>+{fmtNum(r.bpXp)}</b>
                    <span>Pass XP</span>
                  </div>
                )}
              </div>
              {(r.questsCompleted.length > 0 || r.achievementsUnlocked.length > 0) && (
                <div class="res-done">
                  {r.questsCompleted.map((q) => (
                    <span key={q} class="res-chip is-quest">
                      <ScrollText size={16} /> {q}
                    </span>
                  ))}
                  {r.achievementsUnlocked.map((a) => (
                    <span key={a} class="res-chip is-ach">
                      <Award size={16} /> {a}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Only public quick play matches are recorded. */}
      {cm && !cm.match.roomCode && <PlayOfTheGame matchId={cm.match.matchId} />}
      <div class="res-actions">
        {cm?.match.roomCode ? (
          <button class="btn big" onClick={() => go('play', { tab: 'rooms' })}>
            <DoorOpen size={22} /> New room
          </button>
        ) : (
          <button
            class="btn big"
            onClick={() => {
              audio.play('ui_click');
              void quickPlay(mode, prefs.value.maps[mode]);
              home();
            }}
          >
            <RotateCcw size={22} /> Play again
          </button>
        )}
        <button
          class="btn primary big"
          onClick={() => {
            audio.play('ui_click');
            home();
          }}
        >
          Continue <ChevronRight size={22} strokeWidth={3} />
        </button>
      </div>
      {celebrate && r && (
        <div class="res-levelup-layer">
          <LevelUpPanel
            from={r.levelBefore}
            to={r.levelAfter}
            rewards={r.levelRewards}
            onClose={() => {
              audio.play('ui_click');
              setCelebrate(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
